import React from 'react'
import { IconType } from 'react-icons'
import {
  SiJavascript,
  SiTypescript,
  SiReact,
  SiNodedotjs,
  SiMongodb,
  SiAmazon,
  SiGit,
  SiDocker,
  SiGraphql,
  SiCss3,
  SiHtml5,
  SiExpress,
  SiMobx,
  SiRedux,
  SiJest,
  SiTailwindcss,
  SiStyledcomponents,
  SiWebpack,
} from 'react-icons/si'

export interface Skill {
  id: string
  name: string
  category:
    | 'Frontend'
    | 'Backend'
    | 'Database'
    | 'DevOps'
    | 'Tools'
    | 'Languages'
    | 'Soft Skills'
  level: 'Expert' | 'Advanced' | 'Intermediate' | 'Basic'
  yearsOfExperience: number
  description: string
  icon: typeof SiJavascript // This ensures we only use react-icons
  featured: boolean
  technologies: string[]
}

export const skills: Skill[] = [
  {
    id: 'react',
    name: 'React',
    category: 'Frontend',
    level: 'Expert',
    yearsOfExperience: 10,
    description:
      'Deep knowledge of React ecosystem, including hooks, context, performance optimization, and state management.',
    icon: SiReact,
    featured: true,
    technologies: ['React', 'Redux', 'Next.js', 'React Query'],
  },
  {
    id: 'javascript',
    name: 'JavaScript',
    category: 'Languages',
    level: 'Expert',
    yearsOfExperience: 10,
    description:
      'Extensive experience with modern JavaScript (ES6+), including async programming, functional programming, and advanced language features.',
    icon: SiJavascript,
    featured: true,
    technologies: ['ES6+', 'TypeScript', 'Node.js', 'React', 'Vue.js'],
  },
  {
    id: 'typescript',
    name: 'TypeScript',
    category: 'Languages',
    level: 'Expert',
    yearsOfExperience: 8,
    description:
      'Strong expertise in TypeScript, including advanced type system features, generics, and type inference.',
    icon: SiTypescript,
    featured: true,
    technologies: ['TypeScript', 'React', 'Node.js', 'Express'],
  },
  {
    id: 'css',
    name: 'CSS',
    category: 'Frontend',
    level: 'Expert',
    yearsOfExperience: 10,
    description:
      'Deep knowledge of modern CSS (including CSS3, Flexbox, Grid, animations, and responsive design).',
    icon: SiCss3,
    featured: true,
    technologies: [
      'CSS3',
      'Flexbox',
      'Grid',
      'Sass',
      'Tailwind',
      'Styled-Components',
    ],
  },
  {
    id: 'html5',
    name: 'HTML5',
    category: 'Frontend',
    level: 'Expert',
    yearsOfExperience: 10,
    description:
      'Extensive experience with modern HTML5, semantic markup, and accessibility.',
    icon: SiHtml5,
    featured: true,
    technologies: ['HTML5', 'Semantic HTML', 'Accessibility'],
  },
  {
    id: 'node',
    name: 'Node.js',
    category: 'Backend',
    level: 'Advanced',
    yearsOfExperience: 6,
    description:
      'Experience building scalable backend services with Node.js, including REST APIs and real-time applications.',
    icon: SiNodedotjs,
    featured: true,
    technologies: ['Node.js', 'Express', 'Socket.io', 'NestJS'],
  },
  {
    id: 'mongodb',
    name: 'MongoDB',
    category: 'Database',
    level: 'Advanced',
    yearsOfExperience: 4,
    description:
      'Proficient in MongoDB database design, optimization, and integration with Node.js applications.',
    icon: SiMongodb,
    featured: true,
    technologies: ['MongoDB', 'Mongoose', 'Atlas', 'Realm'],
  },
  {
    id: 'aws',
    name: 'AWS',
    category: 'DevOps',
    level: 'Intermediate',
    yearsOfExperience: 3,
    description:
      'Experience with AWS services including EC2, S3, Lambda, and CloudFront for deploying and scaling applications.',
    icon: SiAmazon,
    featured: true,
    technologies: ['EC2', 'S3', 'Lambda', 'CloudFront'],
  },
  {
    id: 'git',
    name: 'Git',
    category: 'Tools',
    level: 'Expert',
    yearsOfExperience: 10,
    description:
      'Expert in Git version control, including advanced branching strategies, CI/CD integration, and team workflows.',
    icon: SiGit,
    featured: true,
    technologies: ['Git', 'GitHub', 'GitLab', 'Bitbucket'],
  },
  {
    id: 'docker',
    name: 'Docker',
    category: 'DevOps',
    level: 'Intermediate',
    yearsOfExperience: 5,
    description:
      'Experience with Docker containerization, including multi-stage builds, Docker Compose, and container orchestration.',
    icon: SiDocker,
    featured: true,
    technologies: ['Docker', 'Docker Compose', 'Kubernetes'],
  },
  {
    id: 'graphql',
    name: 'GraphQL',
    category: 'Backend',
    level: 'Advanced',
    yearsOfExperience: 5,
    description:
      'Experience building and consuming GraphQL APIs, including schema design, resolvers, and client-side integration.',
    icon: SiGraphql,
    featured: true,
    technologies: ['GraphQL', 'Apollo', 'Prisma', 'Hasura'],
  },

  {
    id: 'express',
    name: 'Express',
    category: 'Backend',
    level: 'Advanced',
    yearsOfExperience: 5,
    description:
      'Experience building RESTful APIs and backend services using Express.',
    icon: SiExpress,
    featured: true,
    technologies: ['Express', 'RESTful APIs', 'Node.js'],
  },
  {
    id: 'restful',
    name: 'RESTful APIs',
    category: 'Backend',
    level: 'Advanced',
    yearsOfExperience: 5,
    description: 'Experience designing and consuming RESTful APIs.',
    icon: SiNodedotjs,
    featured: true,
    technologies: ['REST', 'Express', 'Node.js'],
  },
  {
    id: 'react-context',
    name: 'React Context API',
    category: 'Frontend',
    level: 'Advanced',
    yearsOfExperience: 5,
    description: 'Experience using React Context API for state management.',
    icon: SiReact,
    featured: true,
    technologies: ['React', 'Context API'],
  },
  {
    id: 'mobx',
    name: 'Mobx',
    category: 'Frontend',
    level: 'Advanced',
    yearsOfExperience: 5,
    description:
      'Experience using Mobx for state management in React applications.',
    icon: SiMobx,
    featured: true,
    technologies: ['Mobx', 'React'],
  },
  {
    id: 'redux',
    name: 'Redux',
    category: 'Frontend',
    level: 'Intermediate',
    yearsOfExperience: 3,
    description:
      'Experience using Redux for state management in React applications.',
    icon: SiRedux,
    featured: true,
    technologies: ['Redux', 'React'],
  },
  {
    id: 'jest',
    name: 'Jest',
    category: 'Tools',
    level: 'Advanced',
    yearsOfExperience: 5,
    description: 'Experience writing unit tests using Jest.',
    icon: SiJest,
    featured: true,
    technologies: ['Jest', 'Unit Testing'],
  },
  {
    id: 'component-lifecycle',
    name: 'Component Lifecycle',
    category: 'Frontend',
    level: 'Expert',
    yearsOfExperience: 10,
    description:
      'Deep knowledge of React component lifecycle (including hooks, class lifecycle methods, and performance optimization).',
    icon: SiReact,
    featured: true,
    technologies: ['React', 'Hooks', 'Class Lifecycle'],
  },
  {
    id: 'material-ui',
    name: 'Material-UI',
    category: 'Frontend',
    level: 'Intermediate',
    yearsOfExperience: 3,
    description: 'Experience using Material-UI (MUI) for building React UIs.',
    icon: SiReact,
    featured: true,
    technologies: ['Material-UI', 'React'],
  },
  {
    id: 'tailwind',
    name: 'Tailwind',
    category: 'Frontend',
    level: 'Intermediate',
    yearsOfExperience: 3,
    description: 'Experience using Tailwind CSS for rapid UI development.',
    icon: SiTailwindcss,
    featured: true,
    technologies: ['Tailwind CSS', 'CSS'],
  },
  {
    id: 'styled-components',
    name: 'Styled-Components',
    category: 'Frontend',
    level: 'Advanced',
    yearsOfExperience: 6,
    description:
      'Experience using Styled-Components for CSS-in-JS styling in React applications.',
    icon: SiStyledcomponents,
    featured: true,
    technologies: ['Styled-Components', 'CSS-in-JS', 'React'],
  },
  {
    id: 'webpack',
    name: 'Webpack',
    category: 'Tools',
    level: 'Advanced',
    yearsOfExperience: 5,
    description:
      'Experience configuring and optimizing Webpack for bundling and building applications.',
    icon: SiWebpack,
    featured: true,
    technologies: ['Webpack', 'Bundling', 'Build Tools'],
  },
  {
    id: 'soft-communication',
    name: 'Technical Communication',
    category: 'Soft Skills',
    level: 'Expert',
    yearsOfExperience: 10,
    description:
      'Strong ability to communicate technical concepts clearly and effectively.',
    icon: SiJavascript,
    featured: true,
    technologies: ['Communication', 'Documentation', 'Presentations'],
  },
  {
    id: 'soft-requirement',
    name: 'Requirement Analysis',
    category: 'Soft Skills',
    level: 'Advanced',
    yearsOfExperience: 10,
    description:
      'Experience in gathering, analyzing, and translating business requirements into technical solutions.',
    icon: SiJavascript,
    featured: true,
    technologies: ['Requirement Gathering', 'Analysis', 'Business'],
  },
  {
    id: 'soft-mentoring',
    name: 'Team Mentoring',
    category: 'Soft Skills',
    level: 'Advanced',
    yearsOfExperience: 10,
    description:
      'Experience mentoring and coaching team members, fostering growth and collaboration.',
    icon: SiJavascript,
    featured: true,
    technologies: ['Mentoring', 'Coaching', 'Team Collaboration'],
  },
  {
    id: 'soft-collaboration',
    name: 'Cross-functional Collaboration',
    category: 'Soft Skills',
    level: 'Advanced',
    yearsOfExperience: 10,
    description:
      'Experience collaborating across teams (design, product, QA, etc.) to deliver integrated solutions.',
    icon: SiJavascript,
    featured: true,
    technologies: ['Cross-functional', 'Team Collaboration', 'Integration'],
  },
]
